import React, { useState, useEffect } from "react";
import { Box, Button, Checkbox, Grid, Paper, Tab, Tabs, Typography, Switch, Card, CardContent, InputLabel, TextField} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import { getFeatureProfileUrls } from "../api/query";

const EditRoles = ({ role, onBack, onSave, menuItems, disabledRoles }) => {
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [permissions, setPermissions] = useState(role.permissions || {});
  const [adminPermissions, setAdminPermissions] = useState({});
  const [currentUserRole, setCurrentUserRole] = useState("")
  const [settings, setSettings] = useState({})
  const [settingsLoaded, setSettingsLoaded] = useState(false)
  const permissionTypes = ["isEnabled", "canView", "canEdit", "canCreate", "canDelete"];

  useEffect(() => {
    const userData = localStorage.getItem("user");
    const user = userData ? JSON.parse(userData): {}
    setCurrentUserRole(user.roleDetails?.name || "")
    if (user && user.roleDetails && user.roleDetails.permissions) setAdminPermissions(user.roleDetails.permissions)
    else setAdminPermissions({})
    if (Object.keys(permissions).length === 0) {
      const initializePermissions = (modules) => {
        return modules.reduce((acc, module) => {
          acc[module.id] = {
            isEnabled: false,
            permissions: {
              canView: false,
              canEdit: false,
              canCreate: false,
              canDelete: false,
            },
            subModules: module.subModules
              ? initializePermissions(module.subModules)
              : {},
          };
          return acc;
        }, {});
      };

      const initialPermissions = Object.keys(menuItems).reduce((acc, key) => {
        acc[key] = {
          isEnabled: false,
          permissions: {
            canView: false,
            canEdit: false,
            canCreate: false,
            canDelete: false,
          },
          subModules: menuItems[key].subModules
            ? initializePermissions(menuItems[key].subModules)
            : {},
        };
        return acc;
      }, {});

      setPermissions(initialPermissions);
    }
  }, [menuItems, permissions]);
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const sessionData = sessionStorage.getItem("featureSettings");
        if (sessionData) {
          setSettings(JSON.parse(sessionData));
          setSettingsLoaded(true);
          return;
        }
        const settingsData = await getFeatureProfileUrls();
        if (settingsData) {
          setSettings(settingsData);
          sessionStorage.setItem( "featureSettings", JSON.stringify(settingsData));
        }
      } catch (e) {
        console.error("Failed to load feature settings", e);
      } finally {
        setSettingsLoaded(true);
      }
    };

    loadSettings();
  }, []);


  const buildHiddenModules = () => {
    const set = new Set();
    if (!settingsLoaded) return set;

    const hideIfFalse = (flag, moduleId) => {
      if (flag === false) set.add(moduleId);
    };

    hideIfFalse(settings.isStoreSubscribed, "store");
    hideIfFalse(settings.isTableReservationEnabled, "store.tableReservation");
    hideIfFalse(settings.isQrDiningEnabled, "store.qrDining");
    hideIfFalse(settings.isLiveCounterEnabled, "store.orders.liveOrders");
    hideIfFalse(settings.isRewardsSubscribed, "marketing.reward");
    hideIfFalse(settings.isMessagingSubscribed, "marketing.messaging");
    hideIfFalse(settings.isMediaGalleryEnabled, "marketing.mediaGallery");
    hideIfFalse(settings.isAnalyticsSubscribed, "analytics");
    hideIfFalse(settings.showGrowthMenu, "engagement.crm");
    hideIfFalse(settings.isChatBotEnabled, "engagement.intentManager");
    return set;
  };

  const hiddenModules = buildHiddenModules();
  const isModuleHidden = (moduleId) => {
    if (!moduleId) return false;
    const parts = moduleId.split(".");
    let current = "";
    for (let i = 0; i < parts.length; i++) {
      current = i === 0 ? parts[0] : `${current}.${parts[i]}`;
      if (hiddenModules.has(current)) {
        return true;
      }
    }
    return false;
  };

  const handlePermissionChange = (
    moduleId,
    permissionType,
    isParent = false
  ) => {
    setPermissions((prev) => {
      const updatedPermissions = { ...prev };

      const togglePermission = (id, type, value) => {
        const moduleIdParts = id.split(".");
        let currentLevel = updatedPermissions;

        for (let i = 0; i < moduleIdParts.length; i++) {
          const part = moduleIdParts[i];
          if (!currentLevel[part]) {
            currentLevel[part] = {
              isEnabled: false,
              permissions: {
                canView: false,
                canEdit: false,
                canCreate: false,
                canDelete: false,
              },
              subModules: {},
            };
          }
          if (i === moduleIdParts.length - 1) {
            if (type === "isEnabled") {
              currentLevel[part].isEnabled = value;
              if (value) {
                currentLevel[part].permissions.canView = true;
                recursivelyUpdateNonDisabledCheckboxes(getModuleDataFromId(id, menuItems)?.subModules || [], id,"canView",true,updatedPermissions);
              } else {
                disableSubModulePermissions(id, updatedPermissions);
              }
            } else {
              currentLevel[part].permissions[type] = value;
              if (type === "canView" && !value){
                currentLevel[part].isEnabled = false
                currentLevel[part].permissions = {
                  canEdit: false,
                  canCreate: false,
                  canDelete: false,
                };
                disableSubModulePermissions(id, updatedPermissions);
              }
              if (isParent) {
                const moduleData = getModuleDataFromId(id, menuItems);
                if (moduleData?.subModules) {
                  recursivelyUpdateNonDisabledCheckboxes(
                    moduleData.subModules,
                    id,
                    type,
                    value,
                    updatedPermissions
                  );
                }
              }
            }
          } else {
            if (!currentLevel[part].subModules) {
              currentLevel[part].subModules = {};
            }
            currentLevel = currentLevel[part].subModules;
          }
        }
      };

      const newValue = !isPermissionEnabled(moduleId, permissionType);
      togglePermission(moduleId, permissionType, newValue);

      return updatedPermissions;
    });
  };

  const getModuleDataFromId = (moduleId, items) => {
    const parts = moduleId.split(".");
    let current = items[parts[0]];

    for (let i = 1; i < parts.length; i++) {
      if (!current?.subModules) return null;
      current = current.subModules.find((m) => m.id === parts[i]);
      if (!current) return null;
    }

    return current;
  };

  const disableSubModulePermissions = (parentId, permissions) => {
    const moduleIdParts = parentId.split(".");
    let currentLevel = permissions;

    for (let i = 0; i < moduleIdParts.length; i++) {
      const part = moduleIdParts[i];
      if (!currentLevel[part]) {
        return;
      }

      if (i === moduleIdParts.length - 1) {
        currentLevel[part].isEnabled = false;
        currentLevel[part].permissions = {
          canView: false,
          canEdit: false,
          canCreate: false,
          canDelete: false,
        };

        if (currentLevel[part].subModules) {
          Object.keys(currentLevel[part].subModules).forEach((subModuleId) => {
            disableSubModulePermissions(
              `${parentId}.${subModuleId}`,
              permissions
            );
          });
        }
      } else {
        currentLevel = currentLevel[part].subModules;
      }
    }
  };

  const recursivelyUpdateNonDisabledCheckboxes = (
    subModules,
    parentId,
    permissionType,
    value,
    permissions
  ) => {
    subModules.forEach((module) => {
      const fullKey = `${parentId}.${module.id}`;
      const moduleIdParts = fullKey.split(".");
      let currentLevel = permissions;

      if (isPermissionEnabled(fullKey, "isEnabled")) {
        for (let i = 0; i < moduleIdParts.length; i++) {
          const part = moduleIdParts[i];
          if (!currentLevel[part]) {
            currentLevel[part] = {
              isEnabled: false,
              permissions: {
                canView: false,
                canEdit: false,
                canCreate: false,
                canDelete: false,
              },
              subModules: {},
            };
          }
          if (i === moduleIdParts.length - 1) {
            currentLevel[part].permissions[permissionType] = value;
          } else {
            currentLevel = currentLevel[part].subModules;
          }
        }
      }

      if (module.subModules && module.subModules.length > 0) {
        recursivelyUpdateNonDisabledCheckboxes(
          module.subModules,
          fullKey,
          permissionType,
          value,
          permissions
        );
      }
    });
  };

  const isPermissionEnabled = (moduleId, permissionType) => {
    const moduleIdParts = moduleId.split(".");
    let currentLevel = permissions;

    for (let i = 0; i < moduleIdParts.length; i++) {
      const part = moduleIdParts[i];
      if (!currentLevel || !currentLevel[part]) {
        return false;
      }
      if (i === moduleIdParts.length - 1) {
        if (permissionType === "isEnabled") {
          return currentLevel[part]?.isEnabled || false;
        } else {
          return currentLevel[part]?.permissions?.[permissionType] || false;
        }
      } else {
        currentLevel = currentLevel[part]?.subModules || {};
      }
    }

    return false;
  };

  const isPermissionDisabled = (moduleId, permissionType) => {
    if (moduleId.startsWith("dashboard")) return true
    if (permissionType !== "isEnabled") {
      const isModuleEnabled = isPermissionEnabled(moduleId, "isEnabled");
      if (!isModuleEnabled) {
        return true;
      }
    }

    if (permissionType === "isEnabled") {
      const moduleIdParts = moduleId.split(".");
      if (moduleIdParts.length > 1) {
        const parentId = moduleIdParts.slice(0, -1).join(".");
        return !isPermissionEnabled(parentId, "isEnabled");
      }
    }

    return false;
  };

  const hasAccess = (moduleId, permissionType) => {
    if (currentUserRole === "Super Admin") return true;
    const moduleIdParts = moduleId.split(".");
    let currentLevel = adminPermissions;

    for (let i = 0; i < moduleIdParts.length; i++) {
      const part = moduleIdParts[i];
      if (!currentLevel[part])  return false
      if (i === moduleIdParts.length - 1) {
        if (permissionType === "isEnabled") return currentLevel[part][permissionType] || false;
        else return currentLevel[part].permissions[permissionType] || false;
      } else currentLevel = currentLevel[part].subModules;

    }

    return false;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderSubmodules = (subModules, parentKey = "") => {
    if (!Array.isArray(subModules) || subModules.length === 0) {
      return null;
    }

    return subModules.map((module) => {
      const fullKey = parentKey ? `${parentKey}.${module.id}` : module.id;
      const hasNestedModules =
        module.subModules && module.subModules.length > 0;
      const isParent = hasNestedModules;
      const indentLevel = fullKey.split(".").length - 1;
      if (isModuleHidden(fullKey)) return null;
      if (!hasAccess(fullKey, "isEnabled"))  return null

      return (
        <Box key={fullKey}>
          <Grid
            container
            spacing={2}
            sx={{
              py: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
              alignItems: "center",
              bgcolor: isParent ? "grey.100" : "transparent",
            }}
          >
            <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ pl: indentLevel * 2 }}>
                <Typography
                  variant="body2"
                  fontWeight={isParent ? "bold" : "normal"}
                >
                  {module.label}
                </Typography>
              </Box>
            </Grid>

            {permissionTypes.map((type) => (
              <Grid
                key={`${fullKey}-${type}`}
                item
                xs={2}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                {type === "isEnabled" ? (
                  <Switch
                    checked={isPermissionEnabled(fullKey, type)}
                    onChange={() =>
                      handlePermissionChange(fullKey, type, isParent)
                    }
                    disabled={isPermissionDisabled(fullKey, type) ||
                      !hasAccess(fullKey, type)
                    }
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: activeTab === fullKey.split(".")[0] ? "#0d3559" : undefined,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: activeTab === fullKey.split(".")[0] ? "#0d3559" : undefined,
                      }
                    }}
                  />
                ) : (
                <Checkbox
                  checked={isPermissionEnabled(fullKey, type)}
                  onChange={() =>
                    handlePermissionChange(fullKey, type, isParent)
                  }
                  disabled={isPermissionDisabled(fullKey, type) ||
                    !hasAccess(fullKey, type)
                  }
                  size="small"
                  icon={
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        border: 1,
                        borderRadius: 1,
                        borderColor: "black.300",
                      }}
                    />
                  }
                  checkedIcon={
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: 1,
                        bgcolor: "#0d3559",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </Box>
                  }
                />
                )}
              </Grid>
            ))}
          </Grid>

          {hasNestedModules && (
            <Box>{renderSubmodules(module.subModules, fullKey)}</Box>
          )}
        </Box>
      );
    });
  };

  const renderMenuItemsForActiveTab = () => {
    const activeMenuItem = menuItems[activeTab];
    if (!activeMenuItem) return null;
    if (isModuleHidden(activeTab)) return null;

    return (
      <Box key={activeTab}>
        <Grid
          container
          spacing={2}
          sx={{
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            alignItems: "center",
            bgcolor: "grey.200",
          }}
        >
          <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
            <Typography fontWeight="bold">{activeMenuItem.label}</Typography>
          </Grid>

          {permissionTypes.map((type) => (
            <Grid
              key={`${activeTab}-${type}`}
              item
              xs={2}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              {type === "isEnabled" ? (
                <Switch
                  checked={isPermissionEnabled(activeTab, type)}
                  onChange={() => handlePermissionChange(activeTab, type, true)}
                  disabled={
                    isPermissionDisabled(activeTab, type) ||
                    !hasAccess(activeTab, type)
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#0d3559",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#0d3559",
                    }
                  }}
                  size="small"
                />
              ) : (
                <Checkbox
                  checked={isPermissionEnabled(activeTab, type)}
                  onChange={() => handlePermissionChange(activeTab, type, true)}
                  disabled={
                    isPermissionDisabled(activeTab, type) ||
                    !hasAccess(activeTab, type)
                  }
                  size="small"
                  icon={
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        border: 1,
                        borderRadius: 1,
                        borderColor: "black.300",
                      }}
                    />
                  }
                  checkedIcon={
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: 1,
                        bgcolor: "#0d3559",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </Box>
                  }
                />
              )}
            </Grid>
          ))}
        </Grid>
        {renderSubmodules(activeMenuItem.subModules, activeTab)}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3, bgcolor: "grey.50", minHeight: "100vh" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ color: "#0d3559" }}
        >
          Back
        </Button>

        <Button
          variant="contained"
          sx ={{ background:"#0d3559"}}
          onClick={() => onSave({ ...role, permissions, description, name })}
        >
          Save
        </Button>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          {role.name}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {role.description}
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <InputLabel required sx={{ mb: 1, fontSize: 14 }}>
                Name
              </InputLabel>
              <TextField
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter role name"
                size="small"
                disabled={disabledRoles.includes(role.name)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InputLabel sx={{ mb: 1, fontSize: 14 }}>Description</InputLabel>
              <TextField
                fullWidth
                multiline
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Role description"
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTabs-indicator": {
              backgroundColor: "#0d3559",
            },
            "& .MuiTab-root.Mui-selected": {
              color: "#0d3559 !important",
              fontWeight: 600,
            },
          }}
        >
          {Object.entries(menuItems).filter(([key]) => !isModuleHidden(key)).map(([key, item]) => (
            <Tab 
              key={key} 
              label={item.label} 
              value={key}
              sx={{
                textTransform: "none",
                color: "text.primary",
                "&:hover": { color: "#0d3559" },
              }}
            />
          ))}
        </Tabs>

        <Box sx={{ p: 3 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={2}></Grid>

            {permissionTypes.map((type) => (
              <Grid key={type} item xs={2} sx={{ textAlign: "center" }}>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="text.secondary"
                  sx={{ textTransform: "capitalize" }}
                >
                  {type.replace(/([A-Z])/g, " $1").trim()}{" "}
                </Typography>
              </Grid>
            ))}
          </Grid>
          {renderMenuItemsForActiveTab()}
        </Box>
      </Paper>
    </Box>
  );
};

export default EditRoles;
